import { apiBaseUrl } from './apiBaseUrl';

const ENDPOINT = `${apiBaseUrl}/client-monitor/events`;
const MAX_BATCH_SIZE = 20;
const MAX_BATCH_BYTES = 30 * 1024;
const MAX_EVENT_BYTES = 4 * 1024;
const FLUSH_DELAY_MS = 200;
const RETRY_DELAY_MS = 1500;
const MAX_ATTEMPTS = 2;
const SESSION_STORAGE_KEY = 'picsect_client_monitor_session';
const FORBIDDEN_FIELD = /^(authorization|.*token|password|pwd|openid|userid|customeruserid|phone|code|nonce|noncestr|paysign|sign)$/i;

let queue = [];
let flushTimer = null;
let flushing = false;
let sequence = 0;
let memorySessionId = null;

function randomPart() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID().slice(0, 12);
  return Math.random().toString(36).slice(2, 14);
}

function getSessionId() {
  if (memorySessionId) return memorySessionId;
  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      memorySessionId = existing;
      return existing;
    }
    memorySessionId = `web-${Date.now().toString(36)}-${randomPart()}`;
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, memorySessionId);
  } catch (_) {
    memorySessionId = `web-${Date.now().toString(36)}-${randomPart()}`;
  }
  return memorySessionId;
}

function containsForbiddenField(value, seen = new Set()) {
  if (!value || typeof value !== 'object') return false;
  if (seen.has(value)) return true;
  seen.add(value);
  if (Array.isArray(value)) return value.some(item => containsForbiddenField(item, seen));
  return Object.entries(value).some(([key, child]) => (
    FORBIDDEN_FIELD.test(key) || containsForbiddenField(child, seen)
  ));
}

function getPlatform() {
  if (typeof window === 'undefined') return 'web';
  return window.matchMedia?.('(max-width: 719px)').matches ? 'web-mobile' : 'web-desktop';
}

function utf8ByteLength(value) {
  const serialized = typeof value === 'string' ? value : JSON.stringify(value);
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(serialized).length;
  return unescape(encodeURIComponent(serialized)).length;
}

function takeNextBatch() {
  const batch = [];
  while (queue.length && batch.length < MAX_BATCH_SIZE) {
    const candidate = [...batch, queue[0]];
    const body = { events: candidate.map(item => item.event) };
    if (batch.length && utf8ByteLength(body) > MAX_BATCH_BYTES) break;
    batch.push(queue.shift());
  }
  return batch;
}

function scheduleFlush(delay = FLUSH_DELAY_MS) {
  if (flushTimer || typeof window === 'undefined') return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flushClientMonitorEvents();
  }, delay);
}

function requeueFailedBatch(batch) {
  const retryable = batch
    .filter(item => item.attempts + 1 < MAX_ATTEMPTS)
    .map(item => ({ ...item, attempts: item.attempts + 1 }));
  if (retryable.length) queue = [...retryable, ...queue].slice(0, 100);
}

export async function flushClientMonitorEvents() {
  if (flushing || queue.length === 0 || typeof fetch !== 'function') return null;
  flushing = true;
  const batch = takeNextBatch();
  try {
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: batch.map(item => item.event) }),
      keepalive: true,
    });
    if (response.status === 429 || response.status >= 500) {
      requeueFailedBatch(batch);
      scheduleFlush(RETRY_DELAY_MS);
    }
    return response;
  } catch (_) {
    requeueFailedBatch(batch);
    scheduleFlush(RETRY_DELAY_MS);
    return null;
  } finally {
    flushing = false;
    if (queue.length && !flushTimer) scheduleFlush();
  }
}

export function reportClientMonitorEvent(eventName, {
  level = 'info',
  orderRef,
  selectionRef,
  immediate = false,
  ...details
} = {}) {
  if (typeof eventName !== 'string' || !['selection_', 'app_'].some(prefix => eventName.startsWith(prefix))) return null;
  if (!['info', 'warn', 'error'].includes(level)) return null;
  if (containsForbiddenField(details)) return null;

  const sessionId = getSessionId();
  const timestamp = Date.now();
  sequence = (sequence + 1) % 1000000;
  const event = {
    eventId: `${sessionId}-${timestamp.toString(36)}-${sequence.toString(36)}-${randomPart()}`,
    event: eventName,
    level,
    sessionId,
    page: 'web/customer/select',
    timestamp,
    envVersion: process.env.NODE_ENV === 'production' ? 'release' : 'development',
    platform: getPlatform(),
    ...(orderRef ? { orderRef: String(orderRef) } : {}),
    ...(selectionRef ? { selectionRef: String(selectionRef) } : {}),
    ...details,
  };
  if (utf8ByteLength(event) > MAX_EVENT_BYTES) return null;

  queue.push({ event, attempts: 0 });
  if (queue.length > 100) queue = queue.slice(-100);
  if (immediate) {
    flushClientMonitorEvents();
  } else {
    scheduleFlush();
  }
  return event.eventId;
}

export function __resetClientMonitorForTests() {
  if (flushTimer && typeof window !== 'undefined') window.clearTimeout(flushTimer);
  queue = [];
  flushTimer = null;
  flushing = false;
  sequence = 0;
  memorySessionId = null;
}
